import { Box, Typography, useTheme } from '@mui/material'
import { Gavel } from '@mui/icons-material'

interface LogoProps {
  variant?: 'sidebar' | 'header' | 'compact'
  showText?: boolean
}

export default function Logo({ variant = 'sidebar', showText = true }: LogoProps) {
  const theme = useTheme()

  if (variant === 'compact') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
          }}
        >
          <Gavel sx={{ color: 'white', fontSize: 24 }} />
        </Box>
        {showText && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            E-Court
          </Typography>
        )}
      </Box>
    )
  }

  if (variant === 'header') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
          }}
        >
          <Gavel sx={{ color: 'white', fontSize: 20 }} />
        </Box>
        {showText && (
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              E-Court
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.7rem',
                fontWeight: 500,
                letterSpacing: 0.5,
              }}
            >
              N2NAI
            </Typography>
          </Box>
        )}
      </Box>
    )
  }

  // Sidebar variant (default) - Compact version
  return (
    <Box
      sx={{
        p: 2,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.2)',
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          background: 'rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          flexShrink: 0,
        }}
      >
        <Gavel sx={{ fontSize: 24, color: 'white', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }} />
      </Box>
      {showText && (
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: 'white',
              lineHeight: 1.2,
              fontSize: '0.95rem',
              textShadow: '0 1px 2px rgba(0,0,0,0.2)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            E-Court
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: 600,
              letterSpacing: 1.5,
              fontSize: '0.65rem',
              textTransform: 'uppercase',
            }}
          >
            N2NAI
          </Typography>
        </Box>
      )}
    </Box>
  )
}
